import React, { useState, useEffect, useCallback, useRef } from 'react';

// Constants
const SCROLL_THRESHOLD = 300;
const SCROLL_BEHAVIOR = 'smooth' as ScrollBehavior;

const BackToTop: React.FC = () => {
  const [visible, setVisible] = useState(false);

  // Cache DOM references to avoid repeated queries
  const pageContentRef = useRef<HTMLElement | null>(null);
  const appContainerRef = useRef<HTMLElement | null>(null);
  const frameRef = useRef<number | null>(null);

  // Initialize DOM references once
  useEffect(() => {
    pageContentRef.current = document.querySelector('.page-content');
    appContainerRef.current = document.querySelector('.App');
  }, []);

  // Optimized scroll detection with cached references
  const checkScroll = useCallback(() => {
    // Check window scroll (works everywhere)
    const windowScroll = window.scrollY || document.documentElement.scrollTop;

    // Check page-content scroll using cached reference
    const pageContentScroll = pageContentRef.current?.scrollTop || 0;

    // Check App container scroll for desktop layouts
    const appContainerScroll = appContainerRef.current?.scrollTop || 0;

    // Show button if either scroll position is above threshold
    const shouldShow =
      windowScroll > SCROLL_THRESHOLD ||
      pageContentScroll > SCROLL_THRESHOLD ||
      appContainerScroll > SCROLL_THRESHOLD;
    setVisible(shouldShow);
  }, []);

  const scheduleCheck = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      checkScroll();
    });
  }, [checkScroll]);

  useEffect(() => {
    // Throttle all scroll sources to one visibility calculation per animation frame.
    window.addEventListener('scroll', scheduleCheck, { passive: true });

    if (pageContentRef.current) {
      pageContentRef.current.addEventListener('scroll', scheduleCheck, { passive: true });
    }

    if (appContainerRef.current) {
      appContainerRef.current.addEventListener('scroll', scheduleCheck, { passive: true });
    }

    // Initial check
    checkScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', scheduleCheck);
      if (pageContentRef.current) {
        pageContentRef.current.removeEventListener('scroll', scheduleCheck);
      }
      if (appContainerRef.current) {
        appContainerRef.current.removeEventListener('scroll', scheduleCheck);
      }
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [checkScroll, scheduleCheck]);

  const handleClick = useCallback(() => {
    // Scroll both window and page-content to ensure it works everywhere
    window.scrollTo({ top: 0, behavior: SCROLL_BEHAVIOR });

    if (pageContentRef.current) {
      pageContentRef.current.scrollTo({ top: 0, behavior: SCROLL_BEHAVIOR });
    }

    if (appContainerRef.current) {
      appContainerRef.current.scrollTo({ top: 0, behavior: SCROLL_BEHAVIOR });
    }
  }, []);

  if (!visible) return null;

  return (
    <button
      className="back-to-top visible"
      onClick={handleClick}
      aria-label="Back to top"
      type="button"
    >
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="16" fill="#343838"/>
        <path d="M16 22V10M16 10L10 16M16 10L22 16" stroke="#dad4c9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
};

export default BackToTop;